import { useState, type ChangeEvent, type ReactNode } from 'react';
import { ApiError, type ClienteHttp } from './api.js';

/**
 * Importa o cadastro da empresa a partir de uma nota em PDF.
 *
 * O ganho não é digitar menos — é digitar menos **errado**. O CNPJ sai da
 * chave de acesso, que tem dígito verificador, e a razão social e o endereço
 * saem do cadastro da Receita. Os dois juntos cobrem o que mais custa caro
 * quando vai errado para a nota.
 *
 * O que vem do texto do PDF é mostrado como incerto e **não é
 * pré-preenchido**: campo de baixa confiança preenchido é confirmado sem
 * olhar, e uma inscrição estadual errada vai para todas as notas seguintes.
 */

interface CampoSugerido {
  valor: string;
  confianca: 'alta' | 'media' | 'baixa';
  origem: 'chave' | 'receita' | 'texto';
}

interface Sugestao {
  campos: Record<string, CampoSugerido | undefined>;
  chave: { chave: string; modelo: string; serie: number; numero: number } | null;
  avisos: string[];
}

const ORIGEM: Record<CampoSugerido['origem'], string> = {
  chave: 'chave de acesso',
  receita: 'cadastro da Receita',
  texto: 'texto do PDF',
};

const ROTULO: Record<string, string> = {
  cnpj: 'CNPJ',
  razao_social: 'Razão social',
  nome_fantasia: 'Nome fantasia',
  ie: 'Inscrição estadual',
  im: 'Inscrição municipal',
  crt: 'Regime tributário',
  uf: 'UF',
  municipio: 'Município',
  codigo_municipio: 'Código IBGE',
  logradouro: 'Logradouro',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cep: 'CEP',
  telefone: 'Telefone',
};

export function EmitterImport({
  http,
  onImportado,
  onFechar,
}: {
  http: ClienteHttp;
  /** Recebe só os campos confiáveis, para pré-preencher o formulário. */
  onImportado: (campos: Record<string, string>) => void;
  onFechar: () => void;
}): ReactNode {
  const [sugestao, setSugestao] = useState<Sugestao | null>(null);
  const [lendo, setLendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const enviar = async (evento: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;

    setLendo(true);
    setErro(null);
    try {
      const form = new FormData();
      form.append('file', arquivo);
      setSugestao(await http.apiForm<Sugestao>('/v1/emitters/import', form));
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível ler o PDF.');
    } finally {
      setLendo(false);
    }
  };

  const confiaveis = (s: Sugestao): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [nome, campo] of Object.entries(s.campos)) {
      if (campo && campo.confianca === 'alta') out[nome] = campo.valor;
    }
    return out;
  };

  return (
    <div className="card form-card">
      <h3>Importar de uma nota</h3>
      <p className="sub">
        Envie um DANFE em PDF de uma nota que a empresa já emitiu. Lemos a chave de acesso e
        completamos com o cadastro da Receita. Nada é salvo sem a sua confirmação.
      </p>

      {erro && <div className="alert bad">{erro}</div>}

      {!sugestao && (
        <label className="campo">
          <span>Arquivo PDF</span>
          <input className="input" type="file" accept="application/pdf,.pdf" onChange={(e) => void enviar(e)} />
          {lendo && <small className="campo-dica">Lendo o arquivo…</small>}
        </label>
      )}

      {sugestao && (
        <>
          {sugestao.chave && (
            <div className="alert">
              Nota {sugestao.chave.modelo === '65' ? 'NFC-e' : 'NF-e'} nº {sugestao.chave.numero}, série{' '}
              {sugestao.chave.serie}. Chave conferida pelo dígito verificador.
            </div>
          )}

          {sugestao.avisos.map((aviso) => (
            <div className="alert warn" key={aviso}>
              {aviso}
            </div>
          ))}

          <table className="tabela">
            <tbody>
              {Object.entries(sugestao.campos).map(([nome, campo]) =>
                campo ? (
                  <tr key={nome}>
                    <td>{ROTULO[nome] ?? nome}</td>
                    <td>
                      <b>{campo.valor}</b>
                    </td>
                    <td className="sub">
                      {ORIGEM[campo.origem]}
                      {campo.confianca !== 'alta' && ' · confira'}
                    </td>
                  </tr>
                ) : null,
              )}
            </tbody>
          </table>

          <p className="sub">
            Os campos marcados com “confira” vieram do texto do PDF e não entram preenchidos — o
            formulário abre com eles em branco, para você digitar olhando.
          </p>

          <div className="acoes">
            <button className="btn btn-primary" onClick={() => onImportado(confiaveis(sugestao))}>
              Usar estes dados
            </button>
            <button className="btn btn-ghost" type="button" onClick={onFechar}>
              Cancelar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
